import { api } from '../../../core/utils/apiClient';
import {
  TracxnImport,
  PendingCompany,
  VerificationUpdate,
  AuditLog,
} from '../types';

class TracxnVerificationService {
  async uploadFiles(
    files: File[],
    sendNotifications: boolean = true
  ): Promise<TracxnImport[]> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await api.post(
      `/api/v1/verification/tracxn/upload?send_notifications=${sendNotifications}`,
      formData,
      {
        headers: {
          // Let browser set Content-Type with boundary for multipart/form-data
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to upload Tracxn files'
      );
    }

    return response.json();
  }

  async getImports(limit: number = 10, skip: number = 0): Promise<TracxnImport[]> {
    const response = await api.get(
      `/api/v1/verification/tracxn/imports?limit=${limit}&skip=${skip}`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to load import history'
      );
    }

    return response.json();
  }

  async getImportById(importId: number): Promise<TracxnImport> {
    const response = await api.get(
      `/api/v1/verification/tracxn/imports/${importId}`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to load import details'
      );
    }

    return response.json();
  }

  async getPendingCompanies(
    limit: number = 50,
    skip: number = 0
  ): Promise<PendingCompany[]> {
    const response = await api.get(
      `/api/v1/verification/pending?limit=${limit}&skip=${skip}`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to load pending companies'
      );
    }

    const data = await response.json();
    // Handle if response is a string or array
    if (typeof data === 'string') {
      return [];
    }
    return Array.isArray(data) ? data : [];
  }

  async verifyCompany(
    companyId: number,
    update: VerificationUpdate
  ): Promise<any> {
    const response = await api.post(
      `/api/v1/verification/companies/${companyId}/verify`,
      update
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to update verification status'
      );
    }

    return response.json();
  }

  async getAuditLogs(
    companyId?: number,
    action?: string,
    limit: number = 50,
    skip: number = 0
  ): Promise<AuditLog[]> {
    let url = `/api/v1/verification/audit-logs?limit=${limit}&skip=${skip}`;
    if (companyId) url += `&company_id=${companyId}`;
    if (action) url += `&action=${encodeURIComponent(action)}`;

    const response = await api.get(url);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to load audit logs'
      );
    }

    return response.json();
  }

  async getCompanyAuditLogs(companyId: number): Promise<AuditLog[]> {
    const response = await api.get(
      `/api/v1/verification/companies/${companyId}/audit-logs`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to load company audit logs'
      );
    }

    return response.json();
  }

  async sendDailySummary(): Promise<any> {
    const response = await api.post(
      `/api/v1/verification/notifications/send-daily-summary`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to send daily summary'
      );
    }

    return response.json();
  }
}

export const tracxnVerificationService = new TracxnVerificationService();

